/**
 * Helpers for controlled integer-quantity inputs.
 *
 * Background (PD-13758): a number-backed `<input>` renders a literal "0" that
 * snaps right back the instant the field is cleared, because the empty string
 * gets coerced to 0. Users then fight a stubborn leading zero — "the 0 doesn't
 * disappear." The input component fixes the display side by rendering an empty
 * field for 0, but it still has to commit a real number on every change. That
 * is this helper's job: parse, fall back to the minimum when blank/non-numeric,
 * and clamp to the allowed range.
 */

export interface CommitQuantityOptions {
	/** Lowest allowed value. Blank / non-numeric input resolves to this. Defaults to 0. */
	min?: number;
	/** Highest allowed value, when the field is bounded (e.g. delivered <= ordered). */
	max?: number;
}

/**
 * Convert a raw numeric-input string into a clamped integer quantity.
 *
 * - Blank or non-numeric input resolves to `min` (default 0) so callers always
 *   store a real number while the field itself is free to render empty.
 * - Uses base-10 integer parsing (drops any decimal portion).
 * - Clamps to `[min, max]` when those bounds are provided.
 */
export const commitQuantityInput = (
	raw: string,
	options: CommitQuantityOptions = {},
): number => {
	const min = options.min ?? 0;
	const parsed = parseInt(raw, 10);
	const value = Number.isNaN(parsed) ? min : parsed;
	const flooredToMin = Math.max(value, min);
	return options.max != null
		? Math.min(flooredToMin, options.max)
		: flooredToMin;
};
